import { Inject, Injectable } from '@angular/core';
import { TAllUsers } from '@project/view-models';
import cloneDeep from 'lodash/cloneDeep';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { SiteTablePointsService } from 'src/app/project/modules/site/site-table/site-table-points.service';
import { UserService } from 'src/app/project/modules/user/user.service';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { removeSpecialCharactersFromText } from '../../../core/helpers/format-text';
import { ActiveService } from '../../services/active/active.service';
import { TCustomField } from '../custom-fields/custom-fields.model';
import { CustomFieldsService } from '../custom-fields/custom-fields.service';
import { GET_CUSTOM_FIELDS } from '../custom-fields/custom-fields.store';
import { logErrorInSentry } from '../errors/response-error';
import { PointSearchService } from '../points/point-search/point-search.service';
import { TPoint, TPointsByWorkspace } from '../points/points.model';
import { PointsService } from '../points/points.service';
import { getSelectedPoints } from '../points/selected-points';
import { getGroupValueById } from '../site/site-table/body-elements/body-row/getGroupValueById';
import { TColumn } from '../site/site-table/columns/column.model';
import {
  GET_GROUPED_POINTS,
  GET_GROUPING,
  SET_GROUPED_POINTS,
} from '../site/site-table/columns/grouping.store';
import { TableColumnsService } from '../site/site-table/columns/table-columns.service';
import { CustomTableService } from '../site/site-table/custom-table/custom-table.service';
import Table, { FETCH_ALL_PAGE_SIZE } from '../site/site-table/custom-table/table/Table';
import { groupTable } from '../site/site-table/grouping/group';
import { TGroupedPoints } from '../site/site-table/grouping/group-model';
import { checkPointTimelineField } from './local/check-point-timeline-field';
import { fillColumnValue } from './local/fill-column-value';
import { generateGroupCellValue } from './local/generate-group-cell-value';
import { getNumbersExportFormat } from './local/get-numbers-export-format';
import { PointExportFilterService } from './point-export-filter.service';
import { PointExportXLSXService } from './point-export-xlsx.service';

export type TExportColumn = {
  index?: number;
  name: string;
  width?: number;
  timeline?: boolean;
  customFieldIds?: string[];
};

export type TExportColumnWidths = {
  wch: number;
};

export type TColumnFormatMap = { [key: string]: { format: string; type: string } };

@Injectable({
  providedIn: 'root',
})
export class PointExportLocalService {
  columnFormatMap: TColumnFormatMap = {};
  groupDepth = 0;

  constructor(
    @Inject('ORIGIN') private locationOrigin: string,
    private pointsService: PointsService,
    private workspaceService: WorkspaceService,
    private userService: UserService,
    private usersService: UsersService,
    private siteTablePointsService: SiteTablePointsService,
    private pointExportFilterService: PointExportFilterService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private customFieldsService: CustomFieldsService,
    private tableColumnsService: TableColumnsService,
    private customTableService: CustomTableService,
    private pointExportXLSXService: PointExportXLSXService,
    private activeService: ActiveService,
    private pointsSearchService: PointSearchService,
  ) {}

  async exportLocally(type: string): Promise<void> {
    try {
      let allPoints;

      const table = this.customTableService.getTable();
      const workspace = this.workspaceService.getActiveWorkspace();
      const workspaceId = this.activeService.getActiveWorkspaceId();
      const user = this.userService.getUser();
      const selectedPointIds = getSelectedPoints();

      const shouldFetchAllPoints = this.hasNoIndividualSelection(table);

      if (shouldFetchAllPoints) {
        const pageSize = FETCH_ALL_PAGE_SIZE;

        allPoints = await this.pointsSearchService.fetchAllPaginatedPoints(workspaceId, pageSize);
      } else {
        allPoints = this.pointsService.getAllPoints();
      }

      const allPointsCount = allPoints ? Object.keys(allPoints).length : 0;
      const workspaceIdsInStore = allPoints ? Object.keys(allPoints).slice(0, 5).join(',') : '';

      if (allPointsCount === 0) {
        logErrorInSentry({
          message: 'Excel export failed: No points data available in store',
          tags: {
            exportType: type,
            workspaceId: workspaceId || 'overview',
            userEmail: user?.email || 'unknown',
            accountId: user?.accountId || 'unknown',
            hasSelectedAllPoints: String(table.hasSelectedAllPoints),
            selectedPointsCount: String(table.selectedPointsCount),
            selectedPointIdsCount: String(selectedPointIds?.length || 0),
            tablePointsCount: String(table.getPoints()?.length || 0),
            tableVisiblePointIdsCount: String(table.getVisiblePointIds()?.length || 0),
          },
        });
      }

      let points: TPoint[] = [];
      let selectedPoints: TPoint[] = [];

      if (shouldFetchAllPoints) {
        selectedPoints = allPoints;
        points = this.siteTablePointsService.sortPoints(selectedPoints);
      } else {
        selectedPoints = this.getSelectedPoints(workspace, selectedPoints, allPoints);
        ({ points } = this.getPoints(selectedPoints, workspace, allPoints));
      }

      if (points.length === 0) {
        logErrorInSentry({
          message: 'Excel export failed: No points to export after filtering',
          tags: {
            exportType: type,
            workspaceId: workspaceId || 'overview',
            workspaceName: workspace?.siteName || 'overview',
            userEmail: user?.email || 'unknown',
            accountId: user?.accountId || 'unknown',
            allPointsCount: String(allPointsCount),
            workspaceIdsInStore,
            selectedPointsCount: String(selectedPoints.length),
            selectedPointIdsCount: String(selectedPointIds?.length || 0),
            hasSelectedAllPoints: String(table.hasSelectedAllPoints),
            tableItemsCount: String(table.items?.length || 0),
            tablePointsCount: String(table.getPoints()?.length || 0),
            tableVisiblePointIdsCount: String(table.getVisiblePointIds()?.length || 0),
          },
        });

        this.promptService.showWarning('No points available to export');
        return;
      }

      this.handleExportLocally({ points, type, workspace, table });
    } catch (error) {
      logErrorInSentry({
        message: 'Excel export failed with unexpected error',
        error,
        tags: {
          exportType: type,
          errorMessage: error?.message || 'Unknown error',
        },
      });

      const promptText = this.translationPipe.transform('prompt_export_error');
      this.promptService.showError(promptText || 'Export failed. Please try again.');
    }
  }
  private hasNoIndividualSelection(table: Table): boolean {
    return table.selectedPoints.size === 0 || table.hasSelectedAllPoints;
  }

  private getVisibleColumns(): TColumn[] {
    return cloneDeep(
      this.tableColumnsService
        .getColumns()
        .sort((firstColumn, secondColumn) =>
          firstColumn.index > secondColumn.index
            ? 1
            : firstColumn.index < secondColumn.index
              ? -1
              : 0,
        )
        .filter((_column) => !_column.hidden),
    );
  }

  private async handleExportLocally({
    points,
    type,
    workspace,
    table,
  }: {
    points: TPoint[];
    type: string;
    workspace: TWorkspace;
    table: Table;
  }): Promise<void> {
    const promptText = this.translationPipe.transform('prompt_export_start');
    this.promptService.showSuccess(promptText, { duration: 15 });

    const CHUNK_SIZE = 2000;
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    const { name, exportName } = this.getName(workspace, type);

    const workspaces = this.workspaceService.getWorkspaces();
    const user = this.userService.getUser();
    const users = this.usersService.getUsers();

    const baseColumns = this.getVisibleColumns();
    const currentGroupDepth = GET_GROUPING().length;
    this.groupDepth = currentGroupDepth;

    if (baseColumns.length === 0) {
      logErrorInSentry({
        message: 'Excel export failed: No visible columns available',
        tags: {
          exportType: type,
          workspaceId: workspace?.workspaceId || 'overview',
          pointsCount: String(points.length),
        },
      });
    }

    let totalRowsExported = 0;

    for (let i = 0; i < points.length; i += CHUNK_SIZE) {
      const chunk = points.slice(i, i + CHUNK_SIZE);
      const tableToExport: any[][] = [];

      const chunkColumns = cloneDeep(baseColumns);

      await this.fillColumns({
        points: chunk,
        visibleColumns: chunkColumns,
        workspaces,
        users,
        tableToExport,
        type,
        table,
      });

      this.getIndexesToDouble(chunkColumns);
      this.setColumnFormatting(chunkColumns);
      this.setColumnHeaders(chunkColumns, tableToExport);

      totalRowsExported += tableToExport.length - 1; // Subtract header row

      const partNum = Math.floor(i / CHUNK_SIZE) + 1;
      const sheetName = `${name.substring(0, 20)} Part ${partNum}`;

      await this.pointExportXLSXService.addWorksheetToWorkbook({
        workbook,
        tableToExport,
        visibleColumns: chunkColumns,
        groupDepth: currentGroupDepth,
        columnFormatMap: this.columnFormatMap,
        accountName: workspace?.accountName,
        siteName: workspace?.siteName || 'Overview',
        userName: user.userName,
        sheetName: sheetName,
      });

      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (totalRowsExported === 0) {
      logErrorInSentry({
        message: 'Excel export completed but no data rows were added',
        tags: {
          exportType: type,
          workspaceId: workspace?.workspaceId || 'overview',
          workspaceName: workspace?.siteName || 'overview',
          pointsCount: String(points.length),
          visibleColumnsCount: String(baseColumns.length),
        },
      });
    }

    await this.pointExportXLSXService.downloadWorkbook(workbook, exportName);
  }

  private setColumnHeaders(visibleColumns: TExportColumn[], tableToExport: string[][]): void {
    const columnHeaders = visibleColumns.map((column) => column.name);
    columnHeaders.push('Point URL');

    const priorityColumnIndex = columnHeaders.indexOf('Priority');
    const statusColumnIndex = columnHeaders.indexOf('Status');

    if (priorityColumnIndex > -1) {
      columnHeaders[priorityColumnIndex] = 'Priority';
    }

    if (statusColumnIndex > -1) {
      columnHeaders[statusColumnIndex] = 'Status';
    }

    const headersWithGroupSpacing = [];

    for (let i = 1; i <= this.groupDepth; i++) {
      headersWithGroupSpacing.push('');
    }

    tableToExport.unshift([...headersWithGroupSpacing, ...columnHeaders]);
  }

  private getIndexesToDouble(visibleColumns: TExportColumn[]): void {
    const indexesToDouble: number[] = [];

    for (let i = 0; i < visibleColumns.length; i++) {
      const column = visibleColumns[i];

      if (column.timeline) {
        indexesToDouble.push(i);
      }
    }

    indexesToDouble
      .sort()
      .reverse()
      .forEach((index) => {
        visibleColumns.splice(index + 1, 0, {
          name: visibleColumns[index].name + ' (End)',
          width: visibleColumns[index].width,
          customFieldIds: visibleColumns[index].customFieldIds,
        });

        visibleColumns[index].name = visibleColumns[index].name + ' (Start)';
      });
  }

  private async fillColumns({
    points,
    visibleColumns,
    workspaces,
    users,
    tableToExport,
    type,
    table,
  }: {
    points: TPoint[];
    visibleColumns: TColumn[];
    workspaces: TWorkspacesById;
    users: TAllUsers;
    tableToExport: string[][];
    type: string;
    table: Table;
  }): Promise<void> {
    if (this.hasNoIndividualSelection(table)) {
      const hasGrouping = GET_GROUPING().length > 0;

      if (hasGrouping) {
        const previousOrder = GET_GROUPED_POINTS();
        try {
          const groupedPoints = groupTable({ _points: points, pagination: false });
          this.emitGroupedExportRows({
            groups: groupedPoints,
            depth: 1,
            visibleColumns,
            workspaces,
            users,
            tableToExport,
            type,
          });
        } finally {
          SET_GROUPED_POINTS(previousOrder);
        }
      } else {
        points.forEach((point, index) => {
          this.fillWithPointData({
            index,
            pointId: point._id,
            points,
            visibleColumns,
            workspaces,
            users,
            tableToExport,
            type,
            visiblePoints: null,
          });
        });
      }
    } else {
      table.getSelectedItems().forEach((item) => {
        // ToDo change the items of table to use point ID instead of index of point
        if (item && (item.index >= 0 || (item.pointId && table.selectedPoints.has(item.pointId)))) {
          const resolvedPointId = item.pointId || table.getPoints()[item.index]?._id;
          this.fillWithPointData({
            index: item.index,
            pointId: resolvedPointId,
            points,
            visibleColumns,
            workspaces,
            users,
            tableToExport,
            type,
            visiblePoints: null,
          });
        } else if (item && item.id) {
          const GroupValue = getGroupValueById(item.id);

          this.fillWithPointGroupData(
            GroupValue.pointObjects,
            visibleColumns,
            tableToExport,
            GroupValue.value,
            GroupValue.depth,
          );
        }
      });
    }
  }

  private emitGroupedExportRows({
    groups,
    depth,
    visibleColumns,
    workspaces,
    users,
    tableToExport,
    type,
  }: {
    groups: TGroupedPoints[];
    depth: number;
    visibleColumns: TColumn[];
    workspaces: TWorkspacesById;
    users: TAllUsers;
    tableToExport: string[][];
    type: string;
  }): void {
    groups.forEach((group) => {
      const pointObjects: TPoint[] = group.pointObjects ?? [];

      this.fillWithPointGroupData(pointObjects, visibleColumns, tableToExport, group.value, depth);

      if (group.group?.length) {
        this.emitGroupedExportRows({
          groups: group.group,
          depth: depth + 1,
          visibleColumns,
          workspaces,
          users,
          tableToExport,
          type,
        });
      } else {
        pointObjects.forEach((point, index) => {
          this.fillWithPointData({
            index,
            pointId: point._id as string,
            points: pointObjects,
            visibleColumns,
            workspaces,
            users,
            tableToExport,
            type,
            visiblePoints: null as unknown as string[],
          });
        });
      }
    });
  }

  private fillWithPointGroupData(
    points: TPoint[],
    visibleColumns: TColumn[],
    tableToExport: string[][],
    groupTitle: string,
    groupLevel: number,
  ): void {
    const newArray: any[] = [];

    // Fills and indents first cols based on group depth
    for (let i = 1; i <= this.groupDepth; i++) {
      if (i === groupLevel) {
        newArray.push(groupTitle + '(' + points.length + ')');
      } else {
        newArray.push('');
      }
    }

    const customFields = GET_CUSTOM_FIELDS();

    visibleColumns.forEach((column: TColumn & { timeline: boolean }) => {
      column.timeline = checkPointTimelineField(column, this.customFieldsService);

      if (column.timeline) {
        newArray.push('');
        newArray.push('');
        return;
      }

      const groupCellValue = generateGroupCellValue(column, points, customFields);
      const formattedGroupCellValue = groupCellValue
        ? isNaN(Number(groupCellValue))
          ? groupCellValue
          : Number(groupCellValue)
        : groupCellValue;

      newArray.push(formattedGroupCellValue);
    });

    tableToExport.push(newArray);
  }

  private fillWithPointData({
    index,
    pointId,
    points,
    visibleColumns,
    workspaces,
    users,
    tableToExport,
    type,
    visiblePoints,
  }: {
    index: number;
    pointId: string;
    points: TPoint[];
    visibleColumns: TColumn[];
    workspaces: TWorkspacesById;
    users: TAllUsers;
    tableToExport: (string | number)[][];
    type: string;
    visiblePoints: string[];
  }): void {
    const point = (pointId && points.find((p) => p._id === pointId)) || points[index];

    if (!point) {
      return;
    }

    if (visiblePoints && !visiblePoints.includes(point._id)) {
      return;
    }

    const newArray: (string | number)[] = [];

    for (let i = 1; i <= this.groupDepth; i++) {
      newArray.push('');
    }

    visibleColumns.forEach((column: TColumn & { timeline?: boolean }) => {
      const value = fillColumnValue(
        column,
        point,
        workspaces,
        users,
        newArray,
        type,
        this.customFieldsService,
      );

      const formattedValue = value ? (isNaN(Number(value)) ? value : Number(value)) : value;
      newArray.push(formattedValue);
    });

    newArray.push(this.locationOrigin + '/#/site/' + point.workspaceRef.id + '/point/' + point._id);

    tableToExport.push(newArray);
  }

  private getPoints(
    selectedPoints: TPoint[],
    workspace: TWorkspace,
    allPoints: TPointsByWorkspace,
  ): {
    points: TPoint[];
  } {
    let points = [];
    let visiblePoints = [];

    if (selectedPoints.length > 0) {
      points = this.siteTablePointsService.sortPoints(selectedPoints);
    } else {
      if (workspace) {
        visiblePoints = this.customTableService.getTable().getPoints();
      } else {
        const visiblePointsIds = this.customTableService.getTable().getVisiblePointIds();

        Object.keys(allPoints).forEach((key) => {
          visiblePoints = [
            ...visiblePoints,
            ...allPoints[key].entities.filter((point) => visiblePointsIds.includes(point._id)),
          ];
        });
      }

      if (visiblePoints.length > 0) {
        points = this.siteTablePointsService.sortPoints(visiblePoints);
      } else {
        if (workspace) {
          points = this.pointsService.getPoints();
        } else {
          Object.keys(allPoints).forEach((key) => {
            points = [...points, ...allPoints[key].entities];
          });
        }

        points = this.siteTablePointsService.sortPoints(points);
      }
    }

    points = this.pointExportFilterService.filterPoints(points);
    return { points };
  }

  private getSelectedPoints(
    workspace: TWorkspace,
    selectedPoints: TPoint[],
    allPoints: TPointsByWorkspace,
  ): TPoint[] {
    if (workspace) {
      selectedPoints = this.pointsService.getSelectedPoints();
    } else {
      const selectedPointsIds = getSelectedPoints();

      Object.keys(allPoints).forEach((key) => {
        selectedPoints = [
          ...selectedPoints,
          ...allPoints[key].entities.filter((point) => selectedPointsIds.includes(point._id)),
        ];
      });
    }
    return selectedPoints;
  }

  private getName(
    workspace: TWorkspace,
    type: string,
  ): {
    name: string;
    exportName: string;
  } {
    let name = 'overview_export.' + type;
    let exportName = name;

    if (workspace) {
      const accountName = workspace.accountName;
      const siteName = workspace.siteName;

      name = accountName + ' - ' + siteName + '.' + type;
      exportName = name;

      if (name.length > 30) {
        name = siteName + '.' + type;
      }

      if (name.length > 30) {
        name = 'site_export.' + type;
      }
    }

    name = removeSpecialCharactersFromText(name);
    return { name, exportName };
  }

  private setColumnFormatting(visibleColumns: TColumn[]): void {
    this.columnFormatMap = {};

    visibleColumns.forEach((column, columnIndex) => {
      columnIndex = columnIndex + this.groupDepth;
      if (column.customFieldIds) {
        const customFieldId = column.customFieldIds[0];
        const customField = GET_CUSTOM_FIELDS()[customFieldId];

        switch (customField.type) {
          case ECustomFieldType.COST: {
            this.formatCost(customField, columnIndex);
            break;
          }
          case ECustomFieldType.NUMBERS:
            this.formatNumbers(columnIndex, customField);
            break;
          case ECustomFieldType.PERCENTAGE:
            this.formatPercentage(columnIndex);

            break;
          case ECustomFieldType.FORMULA:
            switch (customField.outputType) {
              case ECustomFieldType.PERCENTAGE:
                this.formatPercentage(columnIndex);
                break;
              case ECustomFieldType.COST:
                this.formatCost(customField, columnIndex);
                break;
              case ECustomFieldType.NUMBERS:
                this.formatNumbers(columnIndex, customField);
                break;
            }
            break;
          default:
            this.columnFormatMap[columnIndex] = {
              format: '',
              type: '',
            };
        }
      } else {
        this.columnFormatMap[columnIndex] = {
          format: '',
          type: '',
        };
      }
    });
  }

  private formatNumbers(columnIndex: number, customField: TCustomField): void {
    this.columnFormatMap[columnIndex] = {
      format: getNumbersExportFormat(+customField.decimalPlaces, customField.unit),
      type: 'n',
    };
  }

  private formatCost(customField: TCustomField, columnIndex: number): void {
    const currencySymbol = customField.currencySymbol;
    this.columnFormatMap[columnIndex] = {
      format: '"' + currencySymbol + '"#,##0.00_);\\("' + currencySymbol + '"#,##0.00\\)',
      type: 'n',
    };
  }

  private formatPercentage(columnIndex: number): void {
    this.columnFormatMap[columnIndex] = { format: '0%', type: 'n' };
  }
}
